import { forwardRef, useState } from 'react';
import { CheckIcon } from '@radix-ui/react-icons';

import type { ComboboxItemProps } from './combobox.types';

// ============================================================================
// Size configurations (no Tailwind CSS dependency)
// ============================================================================

const ITEM_SIZES = {
  sm: { height: '32px', fontSize: '12px', padding: '0 12px' },
  md: { height: '40px', fontSize: '14px', padding: '0 12px' },
  lg: { height: '48px', fontSize: '16px', padding: '0 12px' },
} as const;

export const ComboboxItem = forwardRef<HTMLDivElement, ComboboxItemProps<string | number>>(
  (
    {
      option,
      isSelected,
      isFocused,
      isDisabled,
      onSelect,
      size = 'md',
      showCheck = true,
    },
    ref
  ) => {
    const [isHovered, setIsHovered] = useState(false);

    const handleClick = () => {
      if (!isDisabled) {
        onSelect();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!isDisabled) {
          onSelect();
        }
      }
    };

    const IconComponent = option.icon;
    const sizeConfig = ITEM_SIZES[size] || ITEM_SIZES.md;

    // Compute background color based on state
    let backgroundColor = 'transparent';
    if (isDisabled) {
      backgroundColor = 'transparent';
    } else if (isSelected && (isFocused || isHovered)) {
      backgroundColor = '#DBEAFE'; // blue-100
    } else if (isSelected) {
      backgroundColor = '#EFF6FF'; // blue-50
    } else if (isFocused || isHovered) {
      backgroundColor = '#F3F4F6'; // gray-100
    }

    const itemStyle: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      width: '100%',
      height: sizeConfig.height,
      padding: sizeConfig.padding,
      fontSize: sizeConfig.fontSize,
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      transition: 'background-color 150ms ease',
      userSelect: 'none',
      backgroundColor,
      color: isSelected ? '#1E3A8A' : '#1F2937', // blue-900 : gray-800
      fontWeight: isSelected ? 500 : 400,
      opacity: isDisabled ? 0.5 : 1,
    };

    const iconStyle: React.CSSProperties = {
      flexShrink: 0,
      width: '16px',
      height: '16px',
    };

    const contentStyle: React.CSSProperties = {
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
    };

    const labelStyle: React.CSSProperties = {
      display: 'block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    };

    const descriptionStyle: React.CSSProperties = {
      display: 'block',
      fontSize: '12px',
      color: '#6B7280', // gray-500
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    };

    const checkStyle: React.CSSProperties = {
      flexShrink: 0,
      width: '16px',
      height: '16px',
      color: '#2563EB', // blue-600
    };

    return (
      <div
        ref={ref}
        role="option"
        aria-selected={isSelected}
        aria-disabled={isDisabled}
        data-focused={isFocused}
        style={itemStyle}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        tabIndex={isFocused ? 0 : -1}
      >
        {/* Icon (optional) */}
        {IconComponent && (
          <span style={iconStyle}>
            {typeof IconComponent === 'string' ? (
              <span style={{ fontSize: '16px' }}>{IconComponent}</span>
            ) : (
              <IconComponent size={16} />
            )}
          </span>
        )}

        {/* Content */}
        <div style={contentStyle}>
          <span style={labelStyle}>{option.label}</span>
          {option.description && <span style={descriptionStyle}>{option.description}</span>}
        </div>

        {/* Selected indicator */}
        {showCheck && isSelected && (
          <span style={checkStyle} data-testid="check-icon">
            <CheckIcon width={16} height={16} aria-hidden="true" />
          </span>
        )}
      </div>
    );
  }
);

ComboboxItem.displayName = 'ComboboxItem';
